import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { Box } from '@mui/material';

export interface OverlayBox {
  // Row id — a class can have several boxes (e.g. 3 wallabies in one photo),
  // so this (not className) is what identifies one particular box.
  id: string;
  className: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
}

interface BoundingBoxOverlayProps {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  boxes: OverlayBox[];
  // When set, the overlay is in "draw a new box for this class" mode —
  // dragging on empty space creates a box and reports it via onBoxDrawn.
  // Always creates an ADDITIONAL box, even if this class already has one.
  drawingClassName: string | null;
  // Color for the live "draw" preview — the class being drawn has no entry
  // in `boxes` yet (it's created on mouseup), so there's nothing to look a
  // color up from until then.
  drawingColor?: string;
  onBoxDrawn: (className: string, box: { x1: number; y1: number; x2: number; y2: number }) => void;
  onBoxChanged: (id: string, box: { x1: number; y1: number; x2: number; y2: number }) => void;
}

type DragMode = 'draw' | 'move' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se';
type Rect = { x1: number; y1: number; x2: number; y2: number };
// id is null while drawing a brand new box (nothing to match yet) — className
// is still tracked so the live preview can show the right label/color.
type DragState = { mode: DragMode; id: string | null; className: string; startX: number; startY: number; original: Rect; current: Rect };

const HANDLE_SIZE = 10;
const MIN_BOX_SIZE = 5;

// Recomputes a box edge-by-edge from an immutable snapshot taken at drag
// start, using the *total* delta from the drag's start position — never
// from whatever the box happened to be on the previous mousemove.
function applyDrag(mode: DragMode, original: Rect, dx: number, dy: number, imageWidth: number, imageHeight: number): Rect {
  // 'draw' starts from a zero-size box anchored at the mousedown point
  // (original.x1 === original.x2) and has to GROW toward wherever the mouse
  // currently is — not translate a fixed-size box like 'move' does.
  if (mode === 'draw') {
    const anchorX = original.x1;
    const anchorY = original.y1;
    const currentX = anchorX + dx;
    const currentY = anchorY + dy;
    return {
      x1: Math.max(0, Math.min(anchorX, currentX)),
      y1: Math.max(0, Math.min(anchorY, currentY)),
      x2: Math.min(imageWidth, Math.max(anchorX, currentX)),
      y2: Math.min(imageHeight, Math.max(anchorY, currentY)),
    };
  }

  if (mode === 'move') {
    const w = original.x2 - original.x1;
    const h = original.y2 - original.y1;
    // Clamp the box's *position* (not size) to stay fully inside the image.
    const x1 = Math.max(0, Math.min(imageWidth - w, original.x1 + dx));
    const y1 = Math.max(0, Math.min(imageHeight - h, original.y1 + dy));
    return { x1, y1, x2: x1 + w, y2: y1 + h };
  }

  // resize-*: each handle moves exactly two edges; the opposite corner is
  // the fixed pivot and must never be touched by this branch. Each moving
  // edge is clamped in one min/max chain against BOTH the fixed opposite
  // edge (so a drag past it can't invert into a negative-size rect) and the
  // image bounds.
  let { x1: left, y1: top, x2: right, y2: bottom } = original;
  if (mode === 'resize-nw' || mode === 'resize-sw') {
    left = Math.min(right - MIN_BOX_SIZE, Math.max(0, left + dx));
  }
  if (mode === 'resize-ne' || mode === 'resize-se') {
    right = Math.max(left + MIN_BOX_SIZE, Math.min(imageWidth, right + dx));
  }
  if (mode === 'resize-nw' || mode === 'resize-ne') {
    top = Math.min(bottom - MIN_BOX_SIZE, Math.max(0, top + dy));
  }
  if (mode === 'resize-sw' || mode === 'resize-se') {
    bottom = Math.max(top + MIN_BOX_SIZE, Math.min(imageHeight, bottom + dy));
  }

  return { x1: left, y1: top, x2: right, y2: bottom };
}

const BoundingBoxOverlay: React.FC<BoundingBoxOverlayProps> = ({
  imageUrl, imageWidth, imageHeight, boxes, drawingClassName, drawingColor, onBoxDrawn, onBoxChanged,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  // The drag lives in a ref, not useState — window-level mousemove/mouseup
  // listeners are attached once per drag (see startDrag/effect below) and
  // need to read the CURRENT drag on every event without re-subscribing;
  // a ref sidesteps the stale-closure problem that would otherwise require
  // detaching and reattaching the listener on every single mousemove.
  // forceUpdate is only for the visual re-render — same split Annotator's
  // own editor uses (AnnotatorEditorPage.tsx: drawing/resizing refs + a
  // forceUpdate reducer for the live draw-rectangle preview).
  const dragRef = useRef<DragState | null>(null);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  // Handle size in image-pixel space, scaled from screen pixels so it stays
  // a constant visual size regardless of the image's native resolution.
  const handlePxToImagePx = useCallback((px: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return px;
    return (px / rect.width) * imageWidth;
  }, [imageWidth]);

  // Returns null (rather than a bogus {0,0}) when the SVG isn't laid out
  // yet — e.g. mid-transition inside a Dialog — so callers can bail instead
  // of computing with a zero-sized rect (which would divide by zero).
  const toImageCoords = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    const x = ((clientX - rect.left) / rect.width) * imageWidth;
    const y = ((clientY - rect.top) / rect.height) * imageHeight;
    // Deliberately NOT clamped to [0, imageWidth] here — the drag itself
    // (applyDrag) clamps the resulting BOX to image bounds, but the raw
    // cursor position must stay unclamped so dragging far outside the
    // image (or even outside the browser viewport, since these are
    // window-level listeners) still computes a consistent, continuously
    // increasing/decreasing delta rather than getting stuck at an edge
    // value the moment the cursor first reaches it.
    return { x, y };
  }, [imageWidth, imageHeight]);

  // Attached to `window` for the duration of a drag (not the SVG element),
  // so the drag keeps tracking correctly no matter how far or fast the
  // cursor moves relative to the (fairly small, sidebar-constrained)
  // drawing area — an element-scoped mousemove/mouseup stops firing the
  // moment the cursor is no longer directly over that element, which is
  // exactly what made drags feel like they "lost" the mouse or ended
  // prematurely.
  const handleWindowMouseMove = useCallback((e: MouseEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const p = toImageCoords(e.clientX, e.clientY);
    if (!p) return;
    const dx = p.x - drag.startX;
    const dy = p.y - drag.startY;
    dragRef.current = { ...drag, current: applyDrag(drag.mode, drag.original, dx, dy, imageWidth, imageHeight) };
    forceUpdate();
  }, [imageWidth, imageHeight, toImageCoords]);

  const handleWindowMouseUp = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    const { x1, y1, x2, y2 } = drag.current;
    if (x2 - x1 >= MIN_BOX_SIZE && y2 - y1 >= MIN_BOX_SIZE) {
      if (drag.mode === 'draw') onBoxDrawn(drag.className, { x1, y1, x2, y2 });
      else if (drag.id) onBoxChanged(drag.id, { x1, y1, x2, y2 });
    }
    forceUpdate();
  }, [onBoxDrawn, onBoxChanged]);

  // Attached once for the component's lifetime (not re-subscribed per
  // drag) — handleWindowMouseMove/Up already no-op via `if (!drag) return`
  // when nothing is being dragged, so there's no need to add/remove these
  // per mousedown/mouseup.
  useEffect(() => {
    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
      dragRef.current = null;
    };
  }, [handleWindowMouseMove, handleWindowMouseUp]);

  const startDrag = (mode: DragMode, id: string | null, className: string, startX: number, startY: number, original: Rect) => {
    dragRef.current = { mode, id, className, startX, startY, original, current: original };
    forceUpdate();
  };

  const handleBackgroundMouseDown = (e: React.MouseEvent) => {
    if (!drawingClassName) return;
    e.preventDefault();
    const p = toImageCoords(e.clientX, e.clientY);
    if (!p) return;
    startDrag('draw', null, drawingClassName, p.x, p.y, { x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  const handleBoxMouseDown = (e: React.MouseEvent, box: OverlayBox, mode: DragMode) => {
    e.preventDefault();
    e.stopPropagation();
    const p = toImageCoords(e.clientX, e.clientY);
    if (!p) return;
    startDrag(mode, box.id, box.className, p.x, p.y, { x1: box.x1, y1: box.y1, x2: box.x2, y2: box.y2 });
  };

  const drag = dragRef.current;
  // 'move'/'resize' are editing a box that's already in `boxes` — overlay
  // the live drag position onto it in place. 'draw' has no existing entry
  // (it's only created via onBoxDrawn on mouseup), so it has to be added as
  // an extra synthesized box instead of matched-and-replaced — this is what
  // made nothing appear while actively drawing until mouseup finally
  // committed it.
  const renderedBoxes = drag && drag.mode === 'draw'
    ? [...boxes, { id: '__drawing__', className: drag.className, ...drag.current, color: drawingColor ?? '#999999' }]
    : boxes.map((b) => (drag && drag.id === b.id ? { ...b, ...drag.current } : b));
  const handleSizeImgPx = handlePxToImagePx(HANDLE_SIZE);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {/* aspect-ratio makes this wrapper's own box exactly match the
          image's true rendered shape (browser shrinks it to fit both
          maxWidth and maxHeight while holding the ratio, the same
          "contain" behavior objectFit gives a plain <img> — but applied to
          the CONTAINER here). The <img> and <svg> below then just fill
          that box at 100%/100% with no independent sizing/centering logic
          of their own, so they're GUARANTEED identical bounds — no
          possibility of the two drifting apart the way an <img> sized by
          its own aspect ratio (as a flex child) and an <svg> stretched to
          fill the whole parent did, which is what caused clicks to land
          ~100px off from where the image actually was whenever the parent
          was a different shape than the image (i.e. most of the time).
          Annotator's own editor solves the equivalent problem a different
          way — one <canvas> with a manually-tracked scale/offset applied
          identically to both drawing and hit-testing — but forcing equal
          bounds via CSS is simpler here since there's no canvas draw loop
          to already be computing that transform. */}
      <Box sx={{ position: 'relative', aspectRatio: `${imageWidth} / ${imageHeight}`, maxWidth: '100%', maxHeight: 'calc(100vh - 220px)' }}>
        <Box component="img" src={imageUrl} alt="" sx={{ width: '100%', height: '100%', display: 'block' }} />
        <svg
          ref={svgRef}
          viewBox={`0 0 ${imageWidth} ${imageHeight}`}
          style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            cursor: drawingClassName ? 'crosshair' : 'default',
          }}
          onMouseDown={handleBackgroundMouseDown}
        >
        {renderedBoxes.map((b) => (
          <g key={b.id}>
            <rect
              x={b.x1} y={b.y1} width={Math.max(0, b.x2 - b.x1)} height={Math.max(0, b.y2 - b.y1)}
              fill="none" stroke={b.color} strokeWidth={handlePxToImagePx(2)}
              style={{ cursor: 'move' }}
              onMouseDown={(e) => handleBoxMouseDown(e, b, 'move')}
            />
            <text x={b.x1} y={b.y1 - handleSizeImgPx * 0.4} fill={b.color} fontSize={handlePxToImagePx(14)} fontWeight={600}>
              {b.className}
            </text>
            {([
              ['resize-nw', b.x1, b.y1],
              ['resize-ne', b.x2, b.y1],
              ['resize-sw', b.x1, b.y2],
              ['resize-se', b.x2, b.y2],
            ] as [DragMode, number, number][]).map(([mode, hx, hy]) => (
              <rect
                key={mode}
                x={hx - handleSizeImgPx / 2} y={hy - handleSizeImgPx / 2}
                width={handleSizeImgPx} height={handleSizeImgPx}
                fill={b.color}
                style={{ cursor: mode === 'resize-nw' || mode === 'resize-se' ? 'nwse-resize' : 'nesw-resize' }}
                onMouseDown={(e) => handleBoxMouseDown(e, b, mode)}
              />
            ))}
          </g>
        ))}
        </svg>
      </Box>
    </Box>
  );
};

export default BoundingBoxOverlay;
